import { Alert, Flex, Grid, Skeleton, Table, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatNumber, formatDate } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { AmountCard } from './AmountCard'
import { AwaitingEndpoint } from './AwaitingEndpoint'
import { COMMISSION_RULES_ENDPOINT } from './commissionRuleApi'
import { isEndpointMissing } from './profileApi'
import {
  isKnownKind,
  percentFromBp,
  tierBounds,
  type CommissionRule,
  type CommissionTier,
} from './commissionRule'
import type { CommissionRuleState } from './useMyAccount'

/**
 * «Men necha foizda ishlayapman» — egasi savoli (2026-08-20).
 *
 * <h2>⚠️ QOIDA VA YIG'INDI — IKKI BOSHQA SAVOL</h2>
 * Yonidagi `SummaryCards` «qancha cashback QAYTDI» deb javob beradi, bu
 * blok esa «qanday HISOBLANADI» deb. Ikkinchisisiz kassir birinchisini
 * TEKSHIRA olmaydi: qaytgan summa to'g'rimi yoki yo'q — bilib bo'lmaydi.
 * Shuning uchun ular bir sahifada, yonma-yon turadi.
 *
 * <h2>⚠️ FAQAT KUCHDA TURGAN QOIDA</h2>
 * Server BUTUN TARIXNI qaytaradi (yopilgan qoidalar ham). Ekranga esa
 * bittasi chiqadi — `activeRule`. Tarix «hozir qanday» degan savolga
 * javob bermaydi va uni ko'rsatish kassirni chalg'itardi.
 *
 * <h2>⚠️ QOIDA YO'Q — JIMGINA BO'SH QOLDIRILMAYDI</h2>
 * ADR-032 o'lchovi: Staging'da OLTITA agent qoidasiz turgan va cashback
 * olmagan, buni esa hech kim ko'rmagan. Bo'sh blok o'sha holatni
 * takrorlardi, shuning uchun bu yerda ochiq ogohlantirish turadi.
 */
export function CommissionRulePanel({ state }: { state: CommissionRuleState }) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  if (state.error && isEndpointMissing(state.error)) {
    return (
      <AwaitingEndpoint endpoint={COMMISSION_RULES_ENDPOINT} note={t('profile.ruleAwaiting')} />
    )
  }
  if (state.error) {
    return <LoadError titleKey="profile.ruleFailed" error={state.error} />
  }
  // ⚠️ «Yuklanmoqda» va «qoida yo'q» AJRATILADI: ikkinchisi cashback
  // umuman hisoblanmasligini anglatadi va u ochiq ogohlantirish bilan
  // chiqadi. Ularni bitta bo'sh holatga qo'shish o'sha ogohlantirishni
  // ko'rinmas qilardi.
  if (state.loading || !state.loaded) {
    return <Skeleton active paragraph={{ rows: 2 }} />
  }

  /**
   * Qoidaning bitta qatorlik javobi: «2,5%» yoki «200 AFN».
   *
   * ⚠️ `percentBp` — BAZIS PUNKT, foiz emas: 250 = 2,5%. Bo'lish
   * {@link percentFromBp} da va u qorovul bilan qotirilgan.
   *
   * ⚠️ Qiymat kelmasa NOL YOZILMAYDI: «0%» ishonarli ko'rinadigan
   * yolg'on bo'lardi — kassir cashback yo'q deb o'ylardi.
   */
  const valueText = (percentBp: number | null, fixed: CommissionTier['fixedAmount']): string => {
    if (percentBp !== null) {
      return t('profile.rulePercent', { value: formatNumber(percentFromBp(percentBp)) })
    }
    if (fixed !== null) return formatMoneyValue(fixed)
    return t('profile.ruleValueUnknown')
  }

  const shortText = (rule: CommissionRule): string =>
    rule.kind === 'TIER'
      ? t('profile.ruleTierShort', { tiers: formatNumber(rule.tiers.length) })
      : valueText(rule.percentBp, rule.fixedAmount)

  /**
   * Pog'ona oralig'i — CHEGARA MA'NOSI BILAN.
   *
   * ⚠️ «10 000 gacha» degani aynan 10 000 KEYINGI pog'onaga tushadi
   * (ADR-032 §2). Bu jumla ekranda YOZILADI, izohda emas: aks holda
   * kassir 2 kutib 5 oladi va farqni faqat oy oxirida sezadi.
   */
  const rangeOf = (tiers: readonly CommissionTier[], index: number) => {
    const { from, to } = tierBounds(tiers, index)
    if (to === null) {
      return {
        // Yagona pog'ona (`upTo` ham yo'q) — amalda oddiy foiz/summa.
        // «— va undan yuqori» deb yozish chiziqchani chegara deb
        // ko'rsatardi.
        text:
          from === null
            ? t('profile.ruleTierAny')
            : t('profile.ruleTierFrom', { from: formatMoneyValue(from) }),
        note: null,
      }
    }
    const upper = formatMoneyValue(to)
    return {
      text:
        from === null
          ? t('profile.ruleTierUpTo', { to: upper })
          : t('profile.ruleTierRange', { from: formatMoneyValue(from), to: upper }),
      note: t('profile.ruleTierExcluded', { to: upper }),
    }
  }

  const upcoming = state.upcoming ? (
    <Alert
      type="info"
      showIcon
      message={t('profile.ruleUpcomingTitle')}
      description={t('profile.ruleUpcoming', {
        date: formatDate(state.upcoming.validFrom),
        value: shortText(state.upcoming),
      })}
    />
  ) : null

  const rule = state.active
  if (rule === null) {
    return (
      <Flex vertical gap={12}>
        <Alert
          type="warning"
          showIcon
          message={t('profile.ruleNoneTitle')}
          description={t('profile.ruleNoneText')}
        />
        {upcoming}
      </Flex>
    )
  }

  const since = (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {t('profile.ruleSince', { date: formatDate(rule.validFrom) })}
    </Typography.Text>
  )

  // ⚠️ Notanish tur — YIQILISH EMAS, OCHIQ E'TIROF. Server to'rtinchi
  // turni qo'shsa panel «qoida bor, lekin uni o'qiy olmadim» deydi:
  // bo'sh ekran «qoida yo'q» bo'lib o'qilardi va bu boshqa holat.
  if (!isKnownKind(rule.kind)) {
    return (
      <Flex vertical gap={12}>
        <Alert type="warning" showIcon message={t('profile.ruleUnknownKind', { kind: rule.kind })} />
        {since}
        {upcoming}
      </Flex>
    )
  }

  if (rule.kind !== 'TIER') {
    return (
      <Flex vertical gap={12}>
        <AmountCard
          primary
          label={t('profile.ruleNow')}
          value={valueText(rule.percentBp, rule.fixedAmount)}
          hint={t('profile.ruleEachTopup')}
        />
        {since}
        {upcoming}
      </Flex>
    )
  }

  if (rule.tiers.length === 0) {
    return (
      <Flex vertical gap={12}>
        <Alert type="warning" showIcon message={t('profile.ruleTierEmpty')} />
        {since}
        {upcoming}
      </Flex>
    )
  }

  const rows = rule.tiers.map((tier, index) => ({ tier, ...rangeOf(rule.tiers, index) }))

  return (
    <Flex vertical gap={12}>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {t('profile.ruleTiersHint')}
      </Typography.Text>

      {/* Telefonda jadval emas, qatorlar: ikki ustun 360px da chegara
          izohi bilan birga bir-biriga yopishadi (`OperationsList` bilan
          ayni qaror). */}
      {isMobile ? (
        <Flex vertical gap={8}>
          {rows.map(({ tier, text, note }) => (
            <Flex
              key={tier.seq}
              justify="space-between"
              align="baseline"
              gap={12}
              style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: 8 }}
            >
              <Flex vertical>
                <Typography.Text>{text}</Typography.Text>
                {note ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {note}
                  </Typography.Text>
                ) : null}
              </Flex>
              <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
                {valueText(tier.percentBp, tier.fixedAmount)}
              </Typography.Text>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Table<(typeof rows)[number]>
          dataSource={rows}
          rowKey={({ tier }) => tier.seq}
          pagination={false}
          size="small"
          columns={[
            {
              title: t('profile.ruleCol.range'),
              render: (_v, { text, note }) => (
                <Flex vertical>
                  <span>{text}</span>
                  {note ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {note}
                    </Typography.Text>
                  ) : null}
                </Flex>
              ),
            },
            {
              title: t('profile.ruleCol.value'),
              align: 'right',
              width: 130,
              render: (_v, { tier }) => (
                <Typography.Text strong>
                  {valueText(tier.percentBp, tier.fixedAmount)}
                </Typography.Text>
              ),
            },
          ]}
        />
      )}

      {since}
      {upcoming}
    </Flex>
  )
}
